/**
 * useConversation Hook
 *
 * Manages chat conversation state including:
 * - Messages array
 * - Input text state
 * - User interaction tracking
 * - Phase transition detection (profile-building → product-recommendation)
 * - Greeting ChatMessage generation
 * - Smart prompting for profile completion
 *
 * Part of App.js modularization - Phase 5.5A of TypeScript migration
 *
 * @author Heseda AI Team
 * @version 1.0
 */

import { useState, useEffect, useCallback, useRef, useMemo, Dispatch, SetStateAction, ChangeEvent } from 'react';
import { doc, setDoc, Timestamp } from 'firebase/firestore';
import { calculateProfileCompletion } from '../services/profileService';
import { getNextBestQuestion } from '../utils/smartPrompting';
import { auth, db } from '../services/firebaseConfig';
import { saveMessage, loadMessages, deleteMessagesByIds, clearMessages as clearFirestoreMessages, loadOpenAIConversationId } from '../services/chatHistoryService';
import type { MasterProfile } from '../types/profile';
import type { ChatMessage, LanguageCode } from '../types';
import { SAMPLE_FEATURE_ENABLED } from '../config/adminConfig';

// ============================================================================
// Types
// ============================================================================

/** Skin concern labels for display */
interface ConcernLabels {
  [key: string]: string;
}

/** AI analysis issues structure (legacy format from photo analysis) */
interface LegacyAIAnalysisIssues {
  [key: string]: boolean;
}

/** Legacy profile fields that may exist on older profiles */
interface LegacyProfileFields {
  skinType?: string;
  skinConcern?: string[];
  aiAnalysis?: {
    skinType?: string;
    lastAnalysisDate?: string;
    issues?: LegacyAIAnalysisIssues;
  };
}

/** Extended master profile with legacy fields for backwards compatibility */
type ExtendedMasterProfile = MasterProfile & LegacyProfileFields;

/** Hook configuration options */
interface UseConversationOptions {
  /** Current user's master profile */
  masterProfile: ExtendedMasterProfile;
  /** Current language */
  language: LanguageCode;
  /** Whether user is logged in */
  isLoggedIn: boolean;
  /** Session ID for grouping messages */
  sessionId: string;
}

/** Hook return type */
/** Proactive nudge content shown as a dismissable banner after idle time */
export interface NudgeContent {
  icon: string;
  text: string;
  completion?: number; // profile completion %, if applicable
}

interface UseConversationReturn {
  // State
  messages: ChatMessage[];
  setMessages: Dispatch<SetStateAction<ChatMessage[]>>;
  input: string;
  setInput: Dispatch<SetStateAction<string>>;
  hasUserInteracted: boolean;
  setHasUserInteracted: Dispatch<SetStateAction<boolean>>;
  previousPhase: number | null;
  lastSmartPromptTime: number;
  nudgeContent: NudgeContent | null;
  historyLoaded: boolean;

  // Actions
  getGreetingMessage: (lang: LanguageCode, profile: ExtendedMasterProfile) => Promise<string>;
  handleLanguageChange: (e: ChangeEvent<HTMLSelectElement>, setLanguage: Dispatch<SetStateAction<LanguageCode>>) => Promise<void>;
  addMessage: (message: ChatMessage) => void;
  addMessages: (newMessages: ChatMessage[]) => void;
  clearMessages: () => void;
  dismissNudge: () => void;
  openAIConversationId: string | undefined;
}

// ============================================================================
// Concern Labels (Constants)
// ============================================================================

/** Korean concern labels mapping — covers all format variants (underscore, space, hyphen) */
const concernLabelsKo: ConcernLabels = {
  acne: '여드름',
  'acne scars': '여드름 흉터',
  'acne_scars': '여드름 흉터',
  'acne-scars': '여드름 흉터',
  pores: '모공',
  wrinkles: '주름',
  'fine lines': '잔주름',
  'fine_lines': '잔주름',
  pigmentation: '색소침착',
  'dark spots': '잡티',
  'dark_spots': '잡티',
  'dark-spots': '잡티',
  dryness: '건조',
  dehydration: '수분 부족',
  oiliness: '과다 피지',
  redness: '홍조',
  sensitivity: '민감성',
  'dark-circles': '다크서클',
  'dark_circles': '다크서클',
  'dark circles': '다크서클',
  dullness: '칙칙함',
  elasticity: '탄력 저하',
  sagging: '탄력 저하',
  blemishes: '잡티',
  flakes: '각질',
  other: '기타 고민',
};

/** English concern labels mapping — covers all format variants */
const concernLabelsEn: ConcernLabels = {
  acne: 'Acne',
  'acne scars': 'Acne Scars',
  'acne_scars': 'Acne Scars',
  'acne-scars': 'Acne Scars',
  pores: 'Pores',
  wrinkles: 'Wrinkles',
  'fine lines': 'Fine Lines',
  'fine_lines': 'Fine Lines',
  pigmentation: 'Pigmentation',
  'dark spots': 'Dark Spots',
  'dark_spots': 'Dark Spots',
  'dark-spots': 'Dark Spots',
  dryness: 'Dryness',
  dehydration: 'Dehydration',
  oiliness: 'Oiliness',
  redness: 'Redness',
  sensitivity: 'Sensitivity',
  'dark-circles': 'Dark Circles',
  'dark_circles': 'Dark Circles',
  'dark circles': 'Dark Circles',
  dullness: 'Dullness',
  elasticity: 'Loss of Elasticity',
  sagging: 'Loss of Elasticity',
  blemishes: 'Blemishes',
  flakes: 'Flakes',
  other: 'Other Concerns',
};

/** Skin type English to Korean mapping */
const skinTypeEnToKo: Record<string, string> = {
  dry: '건성',
  oily: '지성',
  combo: '복합성',
  combination: '복합성',
  sensitive: '민감성',
  normal: '중성',
};

/** Skin type Korean to English mapping */
const skinTypeKoToEn: Record<string, string> = {
  건성: 'dry',
  지성: 'oily',
  복합성: 'combo',
  민감성: 'sensitive',
  중성: 'normal',
};

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * Custom hook for managing conversation state and logic
 *
 * @param options - Hook configuration
 * @returns Conversation state and handlers
 *
 * @example
 * ```tsx
 * const {
 *   messages,
 *   setMessages,
 *   input,
 *   setInput,
 *   hasUserInteracted,
 *   setHasUserInteracted,
 *   getGreetingMessage,
 *   handleLanguageChange
 * } = useConversation({ masterProfile, language, isLoggedIn });
 * ```
 */
// If the most recent message is older than this, treat as a stale session:
// skip loading history and show a fresh greeting instead.
const SESSION_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

export function useConversation({
  masterProfile,
  language,
  isLoggedIn,
  sessionId,
}: UseConversationOptions): UseConversationReturn {
  // Core ChatMessage state
  const [messages, setMessagesRaw] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState<string>('');
  const [hasUserInteracted, setHasUserInteracted] = useState<boolean>(false);

  // Phase transition tracking
  const [previousPhase, setPreviousPhase] = useState<number | null>(null);

  // Smart prompting state
  const [lastSmartPromptTime, setLastSmartPromptTime] = useState<number>(0);

  // Ref to track if initial greeting has been set
  const initialGreetingSetRef = useRef<boolean>(false);

  // Keep a ref to the latest masterProfile so timeout callbacks read fresh data
  const masterProfileRef = useRef(masterProfile);
  useEffect(() => { masterProfileRef.current = masterProfile; }, [masterProfile]);

  // OpenAI Conversations API — restored from Firestore on session load
  const [openAIConversationId, setOpenAIConversationId] = useState<string | undefined>(undefined);

  // Track which message IDs have already been persisted to Firestore
  const persistedIdsRef = useRef<Set<string>>(new Set());
  // Guard to prevent duplicate loadMessages calls
  const historyLoadedRef = useRef<boolean>(false);
  // State flag: true once async Firestore load finishes (triggers re-render for greeting)
  const [historyLoaded, setHistoryLoaded] = useState<boolean>(false);

  /**
   * Persist any new messages to Firestore, then forward to real setter.
   * Uses message `id` idempotency — re-saving the same ID is a no-op on Firestore.
   */
  const setMessages: Dispatch<SetStateAction<ChatMessage[]>> = useCallback(
    (action) => {
      setMessagesRaw((prev) => {
        const next = typeof action === 'function' ? action(prev) : action;
        // Fire-and-forget persist for new messages
        const uid = auth.currentUser?.uid;
        if (uid) {
          for (const msg of next) {
            if (msg.id && !msg.isStreaming && !msg.isError && !persistedIdsRef.current.has(msg.id) && (msg.text || msg.imageUrl || msg.type === 'web-search')) {
              persistedIdsRef.current.add(msg.id);
              saveMessage(uid, msg, sessionId).catch((err) =>
                console.warn('Failed to persist message', msg.id, err),
              );
            }
          }
        }
        return next;
      });
    },
    [sessionId],
  );

  // Load conversation history from Firestore on login
  useEffect(() => {
    if (!isLoggedIn) {
      historyLoadedRef.current = false;
      setHistoryLoaded(false);
      persistedIdsRef.current.clear();
      initialGreetingSetRef.current = false;
      setMessagesRaw([]);
      setPreviousPhase(null);
      setLastSmartPromptTime(0);
      setOpenAIConversationId(undefined);
      return;
    }
    if (historyLoadedRef.current) return;
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    historyLoadedRef.current = true;

    // Restore the OpenAI Conversations API conversation ID (independent of message history)
    loadOpenAIConversationId(uid, sessionId).then((convId) => {
      if (convId) setOpenAIConversationId(convId);
    }).catch(() => { /* non-critical */ });

    loadMessages(uid).then((loaded) => {
      if (loaded.length > 0) {
        // Session expiry: if the newest message is older than SESSION_TTL_MS,
        // treat this as a stale session — don't restore old context.
        const newest = loaded[loaded.length - 1];
        const newestTs = newest.timestamp ? new Date(newest.timestamp).getTime() : 0;
        if (Date.now() - newestTs > SESSION_TTL_MS) {
          console.log('🕐 Session expired — starting fresh greeting');
          setOpenAIConversationId(undefined);
          setHistoryLoaded(true);
          return;
        }

        // Restore the OpenAI Conversations API conversation ID only AFTER
        // confirming the session is still fresh (TTL not expired).
        loadOpenAIConversationId(uid, sessionId).then((convId) => {
          if (convId) setOpenAIConversationId(convId);
        }).catch(() => { /* non-critical */ });

        // Returning user — suppress the greeting effect so it doesn't
        // append a redundant greeting below the restored history.
        initialGreetingSetRef.current = true;

        // Clean up streaming artifacts: consecutive bot messages where
        // earlier text is a prefix of the next (partial chunks that were
        // persisted due to a prior bug). Keep only the longest version.
        const clean: ChatMessage[] = [];
        const artifactIds: string[] = [];
        for (let i = 0; i < loaded.length; i++) {
          const cur = loaded[i];
          const nxt = loaded[i + 1];
          if (
            nxt &&
            cur.sender === 'bot' &&
            nxt.sender === 'bot' &&
            cur.text &&
            nxt.text &&
            nxt.text.startsWith(cur.text)
          ) {
            // cur is a partial prefix of nxt — drop it
            artifactIds.push(cur.id);
            continue;
          }
          clean.push(cur);
        }

        // Fire-and-forget: purge artifacts from Firestore
        if (artifactIds.length > 0) {
          console.log(`🧹 Cleaning ${artifactIds.length} streaming artifact(s) from Firestore`);
          deleteMessagesByIds(uid, artifactIds).catch((err) =>
            console.warn('Failed to clean streaming artifacts', err),
          );
        }

        // Dedup by message ID — loadMessages merges across sessions and
        // may return the same greeting/message saved in multiple docs.
        const seen = new Set<string>();
        const deduped: typeof clean = [];
        for (const m of clean) {
          if (!seen.has(m.id)) {
            seen.add(m.id);
            deduped.push(m);
          }
        }

        // Mark loaded IDs as persisted so we don't re-write them
        for (const m of deduped) persistedIdsRef.current.add(m.id);
        setMessagesRaw((prev) => {
          // If greeting was already set, merge loaded history BEFORE it
          if (prev.length > 0) {
            const existingIds = new Set(prev.map((m) => m.id));
            const newOnes = deduped.filter((m) => !existingIds.has(m.id));
            return [...newOnes, ...prev];
          }
          return deduped;
        });
      }
      // Set inside .then() (same microtask as setMessagesRaw) so React
      // batches both updates into one render — prevents the greeting
      // effect from seeing historyLoaded=true with messages still empty.
      setHistoryLoaded(true);
    }).catch((err) => {
      console.warn('Failed to load chat history', err);
      setHistoryLoaded(true);
    });

    // No cleanup — intentionally omit resetting initialGreetingSetRef
    // on StrictMode remount. The ref only resets on explicit logout (above).
  }, [isLoggedIn]);

  /**
   * Generate a personalized greeting ChatMessage based on user profile and context
   * 
   * Logic flow:
   * 1. Phase 2 users (completion >= 50%) are ALWAYS treated as returning users
   *    - They must have visited before to complete their profile
   * 2. Phase 1 users with some profile data AND isReturning flag = returning users
   * 3. Phase 1 users with some data but no isReturning flag = returning (profile proves it)
   * 4. Zero completion = new users
   */
  const getGreetingMessage = useCallback(
    async (lang: LanguageCode, profile: ExtendedMasterProfile): Promise<string> => {
      // Only Korean has full greeting translations; non-ko languages use English templates
      if (lang !== 'ko') lang = 'en' as LanguageCode;
      // Cast to MasterProfile for calculateProfileCompletion (it handles the base fields)
      const completion = calculateProfileCompletion((profile || {}) as MasterProfile);
      const loginCount = parseInt(sessionStorage.getItem('loginCount') || '1', 10);
      const fullName = profile?.identity?.name || '';
      const userName = fullName.split(/\s+/)[0] || '';
      const hasAIAnalysis = !!(profile?.aiAnalysis?.skinType || profile?.aiAnalysis?.lastAnalysisDate);
      
      // Phase determination
      const isPhase2 = completion >= 50;
      
      // Note: If completion > 0, user IS returning (profile data proves it)
      // No need for sessionStorage flag which is unreliable

      // Context-based greeting: reference what we know about the user
      // instead of timezone (which can be unreliable)
      const skinTypeValue = profile?.skin?.type || profile?.skinType || '';
      const skinConcerns = profile?.skin?.concerns || profile?.skinConcern || [];
      const skinGoals = profile?.skin?.skinGoals || [];

      // ========================================
      // CASE 1: New user with zero profile data
      // ========================================
      if (completion === 0) {
        // Edge case: AI analysis but no profile data yet
        if (hasAIAnalysis) {
          return lang === 'en'
            ? "🎯 Welcome to Phase 2! Your AI skin analysis is complete. Let's build your full profile for personalized product recommendations!"
            : '🎯 Phase 2에 오신 것을 환영합니다! AI 피부 분석이 완료되었어요. 맞춤형 추천을 받아볼까요?';
        }

        return lang === 'en'
          ? "Welcome! Let's build your personalized skin profile together. What would you like to tell me about your skin?"
          : '환영합니다! 함께 맞춤형 피부 프로필을 만들어볼까요? 피부에 대해 무엇이든 말씀해주세요!';
      }

      // ========================================
      // CASE 2: Returning user (completion > 0)
      // ========================================
      // Clean, short greeting only — nudge/next-step shown as delayed banner
      if (userName) {
        return lang === 'en'
          ? `Welcome back, ${userName}!`
          : `${userName}님, 다시 오셨네요!`;
      } else if (loginCount > 5) {
        return lang === 'en'
          ? `Great to see you again!`
          : `언제나 반가워요!`;
      }
      return lang === 'en' ? `Welcome back!` : `다시 오셨네요!`;
    },
    []
  );

  // ========================================================================
  // Proactive nudge — shown as dismissable banner after 2 min of inactivity
  // ========================================================================
  const [nudgeContent, setNudgeContent] = useState<NudgeContent | null>(null);
  const nudgeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const nudgeDismissedRef = useRef(false);

  const NUDGE_DELAY_MS = 2 * 60 * 1000; // 2 minutes

  /** Build nudge content based on current profile/phase state */
  const getNudgeContent = useCallback(
    (lang: LanguageCode, profile: ExtendedMasterProfile): NudgeContent | null => {
      if (lang !== 'ko') lang = 'en' as LanguageCode;
      const completion = calculateProfileCompletion((profile || {}) as MasterProfile);
      if (completion === 0) return null; // new users get guidance in greeting itself
      const hasAI = !!(profile?.aiAnalysis?.skinType || profile?.aiAnalysis?.lastAnalysisDate);
      const isP2 = completion >= 50;
      const customerStage = profile?.segment?.customerStage;
      const hasSample = SAMPLE_FEATURE_ENABLED && (
        customerStage === 'sample-requested' || customerStage === 'sample-sent' || customerStage === 'sample-delivered');

      // Phase 3: sample status nudge
      if (isP2 && hasSample) {
        if (customerStage === 'sample-delivered') {
          return { icon: '✅', text: lang === 'en' ? 'Your sample has been delivered! How are you liking the products?' : '샘플이 배송 완료되었습니다! 제품은 마음에 드시나요?' };
        } else if (customerStage === 'sample-sent') {
          return { icon: '🚚', text: lang === 'en' ? 'Your sample is on the way — it should arrive soon!' : '샘플이 배송 중입니다! 곧 도착할 예정이에요.' };
        }
        return { icon: '📦', text: lang === 'en' ? 'Sample request received — we\'re preparing your personalized samples.' : '샘플 요청이 접수되었습니다! 맞춤 샘플을 준비 중이에요.' };
      }

      // Phase 2: product search nudge
      if (isP2) {
        if (hasAI) {
          return { icon: '🎯', text: lang === 'en' ? 'AI analysis complete — click 🔍 Search to find products!' : 'AI 분석 완료! 🔍 검색을 클릭해서 제품을 찾아보세요!' };
        }
        return { icon: '🛍️', text: lang === 'en' ? 'Profile complete — click 🔍 Search or ask me anything!' : '프로필 완성! 🔍 검색을 클릭하거나 무엇이든 물어보세요!' };
      }

      // Phase 1 with AI: encourage profile completion
      if (hasAI) {
        return { icon: '📸', text: lang === 'en' ? 'AI analysis done! Keep completing your profile for full recommendations.' : 'AI 분석 완료! 전체 추천을 받으려면 프로필을 계속 완성해주세요.', completion };
      }

      // Phase 1: profile completion nudge (single line)
      const hasSkinType = !!(profile?.skinType || profile?.skin?.type);
      const hasConcerns = (profile?.skinConcern && profile.skinConcern.length > 0) || (profile?.skin?.concerns && profile.skin.concerns.length > 0);
      const hasAge = !!(profile?.identity?.birthYear || profile?.identity?.ageGroup);
      const missing: string[] = [];
      if (!hasAge) missing.push(lang === 'en' ? 'age' : '나이');
      if (!hasConcerns) missing.push(lang === 'en' ? 'skin concerns' : '피부 고민');
      if (!hasSkinType) missing.push(lang === 'en' ? 'skin type' : '피부 타입');

      if (missing.length > 0) {
        return { icon: '📋', text: lang === 'en' ? `Tell me: ${missing.join(', ')}` : `알려주세요: ${missing.join(', ')}`, completion };
      }
      return { icon: '📋', text: lang === 'en' ? 'Tell me about your routine, budget, or allergies!' : '루틴, 예산, 알레르기 등을 더 알려주세요!', completion };
    },
    []
  );

  const dismissNudge = useCallback(() => {
    setNudgeContent(null);
    nudgeDismissedRef.current = true;
    if (nudgeTimerRef.current) { clearTimeout(nudgeTimerRef.current); nudgeTimerRef.current = null; }
  }, []);

  // Cancel nudge when user sends a message
  useEffect(() => {
    if (hasUserInteracted) {
      dismissNudge();
    }
  }, [hasUserInteracted, dismissNudge]);

  /**
   * Handle language change - saves preference to Firebase, UI updates instantly via i18n
   */
  const handleLanguageChange = useCallback(
    async (
      e: ChangeEvent<HTMLSelectElement>,
      setLanguage: Dispatch<SetStateAction<LanguageCode>>
    ): Promise<void> => {
      const selectedLanguage = e.target.value as LanguageCode;
      setLanguage(selectedLanguage);

      // Save language preference to Firebase for email automation
      // Uses nested objects with merge to update without overwriting other identity fields
      // setDoc with merge creates the document if it doesn't exist (unlike updateDoc)
      const user = auth.currentUser;
      if (user) {
        try {
          const docRef = doc(db, 'masterProfiles', user.uid);
          await setDoc(docRef, {
            identity: { uiLanguage: selectedLanguage },
            meta: { updatedAt: Timestamp.now() }
          }, { merge: true });
          console.log(`✅ Language preference saved to Firebase: ${selectedLanguage}`);
        } catch (error) {
          console.error('Failed to save language preference:', error);
        }
      }

      // UI labels update instantly via i18n; the next AI response will use the new language.
      // No greeting message is added — it was confusing when switching languages
      // back and forth (each switch added a greeting, creating duplicate messages).
    },
    []
  );

  /**
   * Add a ChatMessage to the conversation
   */
  const addMessage = useCallback((ChatMessage: ChatMessage): void => {
    setMessages((prev) => [...prev, ChatMessage]);
  }, []);

  /**
   * Add multiple messages to the conversation
   */
  const addMessages = useCallback((newMessages: ChatMessage[]): void => {
    setMessages((prev) => [...prev, ...newMessages]);
  }, []);

  /**
   * Clear all messages and reset conversation
   */
  const clearMessages = useCallback((): void => {
    setMessagesRaw([]);
    setHasUserInteracted(false);
    initialGreetingSetRef.current = false;
    persistedIdsRef.current.clear();
    setOpenAIConversationId(undefined);
    // Reset nudge so it can re-appear for the next session
    nudgeDismissedRef.current = false;
    setNudgeContent(null);
    if (nudgeTimerRef.current) { clearTimeout(nudgeTimerRef.current); nudgeTimerRef.current = null; }
    const uid = auth.currentUser?.uid;
    if (uid) {
      clearFirestoreMessages(uid).catch((err) =>
        console.warn('Failed to clear Firestore messages', err),
      );
    }
  }, []);

  // Phase transition detection - Show greeting when entering new phase
  // Also handles initial greeting on login
  // Derived state: whether any user messages exist (for phase transition guard)
  const hasUserMessages = useMemo(() => messages.some(m => m.sender === 'user'), [messages]);

  // Extract stable primitives from masterProfile to avoid object-reference re-fires
  const profileCompletion = useMemo(
    () => calculateProfileCompletion(masterProfile),
    [masterProfile],
  );
  const profileLanguage = masterProfile?.identity?.uiLanguage;
  const profileName = masterProfile?.identity?.name;
  const profileEmail = masterProfile?.identity?.email;
  const profileSkinType = masterProfile?.skin?.type;
  const profileConcernsLen = masterProfile?.skin?.concerns?.length ?? 0;

  // Proactive nudge timer — fires after NUDGE_DELAY_MS of user inactivity
  useEffect(() => {
    // Only for returning users (completion > 0) who haven't interacted yet
    if (profileCompletion === 0 || hasUserInteracted || nudgeDismissedRef.current || !isLoggedIn) return;

    // Clear any existing timer
    if (nudgeTimerRef.current) clearTimeout(nudgeTimerRef.current);

    nudgeTimerRef.current = setTimeout(() => {
      if (!nudgeDismissedRef.current && !hasUserInteracted) {
        const content = getNudgeContent(language, masterProfileRef.current);
        if (content) setNudgeContent(content);
      }
    }, NUDGE_DELAY_MS);

    return () => { if (nudgeTimerRef.current) clearTimeout(nudgeTimerRef.current); };
  }, [profileCompletion, hasUserInteracted, isLoggedIn, language, getNudgeContent]);

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return; // Only show greetings when logged in

    // Wait until Firestore history is fully loaded to avoid duplicate greetings on refresh
    if (isLoggedIn && !historyLoaded) return;

    // Wait for profile to load before showing greeting
    const hasProfileData = profileName || profileEmail || profileSkinType || profileConcernsLen > 0;
    
    // If profile has no data yet and we haven't set initial greeting, wait
    // This prevents showing Phase 1 greeting for users who are actually Phase 2
    if (!hasProfileData && !initialGreetingSetRef.current && previousPhase === null) {
      return;
    }

    // CRITICAL FIX: Use language from profile if available, otherwise use prop
    const effectiveLanguage = (profileLanguage || language) as LanguageCode;

    const currentPhase = profileCompletion < 50 ? 1 : 2;

    // INITIAL GREETING: First time setting phase (previousPhase is null)
    if (previousPhase === null) {
      // CRITICAL: Check ref FIRST to prevent any race conditions
      if (initialGreetingSetRef.current) {
        setPreviousPhase(currentPhase);
        return;
      }
      
      // Only set initial greeting if no messages yet
      if (messages.length === 0) {
        initialGreetingSetRef.current = true;
        
        // Deterministic greeting IDs — updater below guards against duplicates by ID
        const phase1Id = `greeting:${uid}:phase1:initial`;
        const phase2Id = `greeting:${uid}:phase2:initial`;

        if (currentPhase === 1) {
          const enGreeting = SAMPLE_FEATURE_ENABLED
            ? `Hello! 👋 I'm your Profile Consultant.\n\nI'll ask you a few questions to understand your skin and recommend the perfect products for you. Plus, complete your profile to get a FREE sample pouch! 🎁\n\nDo you have any skin concerns right now?`
            : `Hello! 👋 I'm your Profile Consultant.\n\nI'll ask you a few questions to understand your skin and recommend the perfect products for you.\n\nDo you have any skin concerns right now?`;
          const koGreeting = SAMPLE_FEATURE_ENABLED
            ? `안녕하세요! 👋 저는 프로필 상담사입니다.\n\n몇 가지 질문으로 고객님의 피부를 파악하고, 완벽한 제품을 추천해 드릴게요. 프로필을 완성하시면 무료 샘플 파우치도 받으실 수 있습니다! 🎁\n\n지금 피부 고민이 있으신가요?`
            : `안녕하세요! 👋 저는 프로필 상담사입니다.\n\n몇 가지 질문으로 고객님의 피부를 파악하고, 완벽한 제품을 추천해 드릴게요.\n\n지금 피부 고민이 있으신가요?`;
          setMessages((prev) => {
            if (prev.some(m => m.id === phase1Id)) return prev;
            if (prev.length > 0) return prev; // Don't drop messages added between outer check and updater
            return [
              {
                id: phase1Id,
                timestamp: new Date().toISOString(),
                sender: 'bot',
                text: effectiveLanguage !== 'ko' ? enGreeting : koGreeting,
              },
            ];
          });
        } else {
          // Phase 2: Product recommendation greeting (async)
          getGreetingMessage(effectiveLanguage, masterProfile).then((greeting) => {
            setMessages((prev) => {
              // ID-based guard: never add a greeting that already exists
              if (prev.some(m => m.id === phase2Id)) return prev;
              if (prev.length > 0) return prev;
              return [{ 
                id: phase2Id,
                timestamp: new Date().toISOString(),
                sender: 'bot', 
                text: greeting 
              }];
            });
          });
        }
      }
      setPreviousPhase(currentPhase);
      return; // Don't continue to transition check
    }

    // PHASE TRANSITION: Only show transition message when ACTUALLY transitioning phases
    if (previousPhase !== currentPhase) {
      // If user hasn't sent any messages yet, this is profile data still loading
      // from Firebase (partial → full), not a real phase transition.
      if (!hasUserMessages) {
        setPreviousPhase(currentPhase);
        return;
      }

      // Phase transition message removed — banner/toast handles this (no duplicate AI message)
      setPreviousPhase(currentPhase);
    }
  // Use stable primitives instead of masterProfile object reference
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileCompletion, profileLanguage, profileName, profileEmail, profileSkinType, profileConcernsLen, previousPhase, language, hasUserMessages, getGreetingMessage, isLoggedIn, historyLoaded]);

  // Smart prompting - periodically suggest profile questions
  // Only fires after user has actually interacted (not from greeting-only states)
  useEffect(() => {
    const now = Date.now();
    // Only prompt once every 30 seconds to avoid spam
    if (now - lastSmartPromptTime < 30000) {
      return;
    }

    // Don't prompt if bot just responded (last message is from bot)
    const lastMessage = messages[messages.length - 1];
    if (!lastMessage || lastMessage.sender === 'bot') {
      return;
    }

    // Don't prompt if there are no user messages (greeting-only state)
    if (!messages.some(m => m.sender === 'user')) {
      return;
    }

    const completion = profileCompletion;

    // Only prompt if we have some conversation but profile is incomplete
    if (completion < 50 && messages.length > 3 && messages.length % 4 === 0) {
      const nextQuestion = getNextBestQuestion(masterProfile as MasterProfile, completion);
      if (nextQuestion) {
        setMessages((prev) => [
          ...prev,
          {
            id: `${auth.currentUser?.uid}:smartprompt:${Date.now()}`,
            timestamp: new Date().toISOString(),
            sender: 'bot',
            text: nextQuestion.question,
            isSystemSuggestion: true,
          },
        ]);
        setLastSmartPromptTime(now);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length, profileCompletion, lastSmartPromptTime]);

  return {
    // State
    messages,
    setMessages,
    input,
    setInput,
    hasUserInteracted,
    setHasUserInteracted,
    previousPhase,
    lastSmartPromptTime,
    nudgeContent,
    historyLoaded,

    // Actions
    getGreetingMessage,
    handleLanguageChange,
    addMessage,
    addMessages,
    clearMessages,
    dismissNudge,
    openAIConversationId,
  };
}

export default useConversation;
